import { useState, useEffect } from 'react';
import {
	X,
	UserPlus,
	MapPin,
	Phone,
	Mail,
	Building2,
	Tag,
	FileText,
} from 'lucide-react';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import SnackbarManager from '@CommonFunctionalDisplay/GlobalSnackbar/SnackbarManager';
import LeadsService from '@Helpers/URLHelpers/LeadsService';
import ErrorHandler from '@Root/ErrorHandler';
import './CreateLeadsForm.scss';

const LEAD_STATUSES = [
	{ key: 'newLeads', label: 'New Leads', color: '#6366f1' },
	{ key: 'contacted', label: 'Contacted', color: '#0ea5e9' },
	{ key: 'followUp', label: 'Follow Up', color: '#f59e0b' },
	{ key: 'active', label: 'Active', color: '#10b981' },
	{ key: 'onHold', label: 'On Hold', color: '#8b5cf6' },
	{ key: 'onboarded', label: 'Onboarded', color: '#14b8a6' },
	{ key: 'closed', label: 'Closed', color: '#64748b' },
	{ key: 'lost', label: 'Lost', color: '#ef4444' },
];

const INITIAL_FORM = {
	name: '',
	companyName: '',
	email: '',
	contactNumber: '',
	address: '',
	status: 'newLeads',
	source: '',
	query: '',
};

export default function CreateLeadsForm(props) {

	const [createForm, setCreateForm] = useState(INITIAL_FORM);
	const [createFormErrors, setCreateFormErrors] = useState({});
	const { currentProjectId } = useReduxProject();
	const { activeUserId } = useReduxActiveUser();
	const leadsService = new LeadsService();
	const errorHandler = new ErrorHandler();

	useEffect(() => {
		if (props.contact) {
			const contact = props.contact;
			setCreateForm({
				...INITIAL_FORM,
				name: contact?.name || '',
				email: contact?.email || '',
				companyName: contact?.companyName || '',
				contactNumber: contact?.contactNumber || '',
				address: contact?.address || '',
				source: contact?.source || '',
				query: contact?.query || '',
			});
		}
	}, [props.contact]);

	const handleCreateLead = async (e) => {
		e.preventDefault();
		const errors = validateCreateForm();
		if (Object.keys(errors).length > 0) {
			setCreateFormErrors(errors);
			return;
		}
		const newLead = {
			...createForm,
			name: createForm.name.trim(),
			email: createForm.email.trim(),
			statusUpdate: '',
			details: '{}',
			comments: '[]',
			reporterUserId: activeUserId,
			projectId: currentProjectId,
		};
		try {
		    let isUpdateForm = !!props?.type;
		    if(isUpdateForm) {
		        newLead.id = props?.contact.id;
		    }
		    console.log(newLead)
		    let response;
		    if(isUpdateForm) {
		        response = await leadsService.updateLeads(newLead);
		    } else {
			    response = await leadsService.createLeads(newLead);
		    }
			if (response.status < 400) {
				response = JSON.parse(response.body);
				const body = parseLeadsData(response);
				if (!props.contact) {
					props?.setLeads((prev) => [body, ...prev]);
					props?.setSelectedLead(body);
				}
                if(!!props.reloadData) {
                    props?.reloadData(response.id);
                }
			} else {
				SnackbarManager.show(response.message, 'error');
				return;
			}
		} catch (err) {
			errorHandler.handleError(err);
			return;
		}
		props?.onClose();
		setCreateForm(INITIAL_FORM);
		setCreateFormErrors({});
	};

	const handleCreateInputChange = (e) => {
		const { name, value } = e.target;
		setCreateForm((prev) => ({
			...prev,
			[name]: value,
		}));
		setCreateFormErrors((prev) => ({
			...prev,
			[name]: undefined,
		}));
	};

	const validateCreateForm = () => {
		const errors = {};
		if (!createForm.name.trim()) {
			errors.name = 'Name is required';
		}
		if (!createForm.status) {
			errors.status = 'Status is required';
		}
		if (!createForm.contactNumber.trim()) {
			errors.contactNumber = 'Phone number is required';
		} else if (
			createForm.contactNumber.length < 10 ||
			createForm.contactNumber.length > 12
		) {
			errors.contactNumber = 'Enter a valid phone number';
		}
		return errors;
	};

	const safeJsonParse = (value, fallback = null) => {
		try {
			if (typeof value === 'string') {
				return JSON.parse(value);
			}
			return value;
		} catch {
			return fallback;
		}
	};

	const parseLeadsData = (item) => ({
		...item,
		details: safeJsonParse(item.details, {}),
		history: safeJsonParse(item.history, []),
		comments: safeJsonParse(item.comments, []),
	});

	const handleOverlayClick = (e) => {
		if (e.target === e.currentTarget) {
			props?.onClose();
		}
	};

	const selectedStatus = LEAD_STATUSES.find(
		(status) => status.key === createForm.status,
	);

	return (
		<div className='lead-modal-overlay' onClick={handleOverlayClick}>
			<div className='lead-modal' onClick={(e) => e.stopPropagation()}>
				{/* Header */}
				<div className='lead-modal__header'>
					<div className='lead-modal__heading'>
						<div className='lead-modal__icon'>
							<UserPlus size={20} strokeWidth={2} />
						</div>

						<div>
							<h2>{props.contact ? 'Edit Lead' : 'Add New Lead'}</h2>
							<p>
								{props.contact
									? 'Update the lead information below'
									: 'Add a new prospect to your pipeline'}
							</p>
						</div>
					</div>

					<button
						type='button'
						className='lead-modal__close'
						onClick={props.onClose}
						aria-label='Close'
					>
						<X size={19} />
					</button>
				</div>

				{/* Form */}
				<form
					className='lead-form'
					onSubmit={handleCreateLead}
					autoComplete='off'
				>
					{/* Contact Information */}
					<div className='form-section'>
						<div className='form-section__header'>
							<div className='form-section__title'>
								<UserPlus size={16} />
								<span>Contact Information</span>
							</div>

							<span className='form-section__required'>* Required</span>
						</div>

						<div className='form-grid'>
							<FormField
								label='Full Name'
								name='name'
								value={createForm.name}
								onChange={handleCreateInputChange}
								error={createFormErrors.name}
								required
								placeholder='Enter full name'
								icon={<UserPlus size={16} />}
							/>

							<FormField
								label='Email Address'
								name='email'
								type='email'
								value={createForm.email}
								onChange={handleCreateInputChange}
								error={createFormErrors.email}
								placeholder='name@company.com'
								icon={<Mail size={16} />}
							/>

							<FormField
								label='Company'
								name='companyName'
								value={createForm.companyName}
								onChange={handleCreateInputChange}
								error={createFormErrors.companyName}
								placeholder='Company name'
								icon={<Building2 size={16} />}
							/>

							<FormField
								label='Phone Number'
								name='contactNumber'
								type='tel'
								value={createForm.contactNumber}
								onChange={handleCreateInputChange}
								error={createFormErrors.contactNumber}
								required
								placeholder='+91 98765 43210'
								icon={<Phone size={16} />}
							/>

							<FormField
								label='Location'
								name='address'
								value={createForm.address}
								onChange={handleCreateInputChange}
								error={createFormErrors.address}
								placeholder='City, State or Address'
								icon={<MapPin size={16} />}
								fullWidth
							/>
						</div>
					</div>

					{/* Lead Information */}
					<div className='form-section'>
						<div className='form-section__header'>
							<div className='form-section__title'>
								<Tag size={16} />
								<span>Lead Information</span>
							</div>
						</div>

						<div className='form-grid'>
							<div className='form-field'>
								<label htmlFor='status'>
									Lead Status
									<span className='required'>*</span>
								</label>

								<div className='select-wrapper'>
									<span
										className='status-dot'
										style={{
											backgroundColor: selectedStatus?.color,
										}}
									/>

									<select
										id='status'
										name='status'
										value={createForm.status}
										onChange={handleCreateInputChange}
										className={createFormErrors.status ? 'has-error' : ''}
									>
										{LEAD_STATUSES.map((status) => (
											<option key={status.key} value={status.key}>
												{status.label}
											</option>
										))}
									</select>
								</div>

								{createFormErrors.status && (
									<span className='field-error'>{createFormErrors.status}</span>
								)}
							</div>

							<FormField
								label='Lead Source'
								name='source'
								value={createForm.source}
								onChange={handleCreateInputChange}
								error={createFormErrors.source}
								placeholder='e.g. Website, Referral, Ads'
								icon={<Tag size={16} />}
							/>
						</div>
					</div>

					{/* Notes */}
					<div className='form-section form-section--notes'>
						<div className='form-section__header'>
							<div className='form-section__title'>
								<FileText size={16} />
								<span>Notes</span>
							</div>
						</div>

						<div className='form-field'>
							<textarea
								name='query'
								value={createForm.query}
								onChange={handleCreateInputChange}
								placeholder='Add any additional information about this lead...'
								rows={4}
							/>
						</div>
					</div>

					{/* Footer */}
					<div className='lead-modal__footer'>
						<button
							type='button'
							className='btn btn--secondary'
							onClick={props.onClose}
						>
							Cancel
						</button>

						<button type='submit' className='btn btn--primary'>
							<UserPlus size={17} />
							{props.contact ? 'Update Lead' : 'Create Lead'}
						</button>
					</div>
				</form>
			</div>
		</div>
	);
}

function FormField({
	label,
	name,
	value,
	onChange,
	error,
	required = false,
	placeholder,
	type = 'text',
	icon,
	fullWidth = false,
}) {
	return (
		<div className={`form-field ${fullWidth ? 'form-field--full' : ''}`}>
			<label htmlFor={name}>
				{label}
				{required && <span className='required'>*</span>}
			</label>

			<div className={`input-wrapper ${error ? 'has-error' : ''}`}>
				{icon && <span className='input-icon'>{icon}</span>}

				<input
					id={name}
					name={name}
					type={type}
					value={value}
					onChange={onChange}
					placeholder={placeholder}
				/>
			</div>

			{error && <span className='field-error'>{error}</span>}
		</div>
	);
}
